import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ProjectTeamMember {
  assignment_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  display_name: string | null;
  email: string;
  profile_photo_url: string | null;
  role_name: string | null;
  role_color: string | null;
  base_level: number | null;
}

export const useProjectTeam = (projectId: string | undefined) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["project_team", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_assignments")
        .select("id, user_id, user_profiles(first_name, last_name, display_name, email, profile_photo_url, roles(name, color, base_level))")
        .eq("project_id", projectId!);
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        assignment_id: row.id,
        user_id: row.user_id,
        first_name: row.user_profiles?.first_name ?? "",
        last_name: row.user_profiles?.last_name ?? "",
        display_name: row.user_profiles?.display_name,
        email: row.user_profiles?.email ?? "",
        profile_photo_url: row.user_profiles?.profile_photo_url,
        role_name: row.user_profiles?.roles?.name ?? null,
        role_color: row.user_profiles?.roles?.color ?? null,
        base_level: row.user_profiles?.roles?.base_level ?? null,
      })) as ProjectTeamMember[];
    },
    enabled: !!projectId,
  });

  const addMember = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await (supabase
        .from("project_assignments") as any)
        .insert({ project_id: projectId!, user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_team", projectId] });
      qc.invalidateQueries({ queryKey: ["projects", projectId] });
    },
  });

  const removeMember = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from("project_assignments")
        .delete()
        .eq("id", assignmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_team", projectId] });
      qc.invalidateQueries({ queryKey: ["projects", projectId] });
    },
  });

  return {
    members: query.data ?? [],
    isLoading: query.isLoading,
    addMember,
    removeMember,
  };
};
